// Last-used package dimensions for partner (automated) shipping.
//
// Sellers ship near-identical parcels order after order, so re-typing weight and
// L/W/H on every ship action is pure friction — and for partners that quote on
// dimensions (WayuVega) it also means waiting on the serviceability call each
// time. We persist the last successfully submitted values and prefill them on
// the next open. Best-effort only: any storage failure just means no prefill.
const CACHE_KEY = "package_dimensions_cache_v1";

export interface PackageDimensions {
  weight: string; // grams
  height: string; // cm
  width: string;  // cm
  length: string; // cm
}

const isNonEmptyNumeric = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value));

export const readLastPackageDimensions = (): PackageDimensions | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PackageDimensions>;
    // Only prefill when every field survived — a partial set would leave the
    // submit button disabled with no obvious reason why.
    if (
      isNonEmptyNumeric(parsed.weight) &&
      isNonEmptyNumeric(parsed.height) &&
      isNonEmptyNumeric(parsed.width) &&
      isNonEmptyNumeric(parsed.length)
    ) {
      return {
        weight: parsed.weight,
        height: parsed.height,
        width: parsed.width,
        length: parsed.length,
      };
    }

    return null;
  } catch {
    return null;
  }
};

export const writeLastPackageDimensions = (dimensions: PackageDimensions): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(dimensions));
  } catch {
    // ignore quota / serialization errors — cache is best-effort
  }
};

export const clearLastPackageDimensions = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
};
